import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function QuoteEmailModal({ open, onOpenChange, modules, total, vertical = null }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/leads", {
        email,
        name: name || null,
        modules: Array.from(modules),
        estimated_monthly_eur: total,
        vertical,
        source: "landing-calculator",
      });
      setDone(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) {
      setEmail(""); setName(""); setDone(null); setError(""); setLoading(false);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="quote-modal">
        {done ? (
          <div className="text-center" data-testid="quote-modal-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center border border-foreground">
              <CheckCircle2 className="h-6 w-6 text-[hsl(var(--accent))]" />
            </div>
            <h2 className="mt-4 font-display text-2xl font-extrabold tracking-tighter">Devis envoyé</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Vérifiez votre boîte de réception <strong>{email}</strong>. Vous y trouverez votre récap modules et un lien d'inscription pré-rempli.
            </p>
            <div className="mt-6">
              <Button onClick={() => handleOpenChange(false)} className="w-full" data-testid="quote-modal-close">
                Fermer
              </Button>
            </div>
            <p className="mt-3 text-[10px] text-muted-foreground">{Array.from(modules).join(" · ")} — {total}€/mois</p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tighter">
                Recevez votre devis personnalisé
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="border border-border bg-secondary p-3">
                <div className="overline">Configuration choisie</div>
                <div className="mt-1 font-mono text-xs">{Array.from(modules).join(" · ")}</div>
                <div className="mt-2 font-display text-2xl font-extrabold tabular-nums">{total}€<span className="text-sm font-medium text-muted-foreground"> / mois</span></div>
              </div>
              <div>
                <Label>Prénom <span className="text-muted-foreground">(optionnel)</span></Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  data-testid="quote-modal-name"
                  placeholder="Jeanne"
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="mt-1"
                  data-testid="quote-modal-email"
                  placeholder="vous@etablissement.fr"
                />
              </div>
              {error && (
                <div className="border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="quote-modal-error">
                  {error}
                </div>
              )}
            </div>
            <DialogFooter className="mt-6">
              <Button type="submit" disabled={loading || !email} className="w-full" data-testid="quote-modal-submit">
                <Mail className="mr-2 h-4 w-4" />
                {loading ? "Envoi…" : "Recevoir mon devis par e-mail"}
              </Button>
            </DialogFooter>
            <p className="mt-3 text-center text-[10px] text-muted-foreground">
              Sans engagement. Nous n'utilisons votre e-mail que pour cette demande.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
